import React, { useState } from 'react';
import { Drawer } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NAV_ITEMS } from './navItems';
import { AccountIcon, MenuIcon } from './icons';

/**
 * The app's three locales, matching `supportedLngs` in src/i18n.
 *
 * Labelled with endonyms and deliberately not run through `t()`: a language
 * picker is read by someone who cannot necessarily read the current language,
 * so "中文" has to say 中文 whichever locale is active.
 */
const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ms', label: 'Bahasa Melayu' },
  { code: 'zh', label: '中文' },
];

/**
 * Mobile header. Hidden at lg, where TopNav takes over.
 *
 * Follows the retail convention the app is modelled on: a slim announcement
 * strip, then a bar with the menu on the left, the wordmark centred and the
 * account entry on the right.
 *
 * Two deliberate omissions, for the same reason SiteFooter omits the
 * reference site's Customer Care / Follow Us columns: there is no search
 * index and no cart in this app, so a search or bag icon would be a control
 * that does nothing. The announcement strip links to /support — a real route
 * — rather than carrying an invented promotion.
 *
 * It does not need `sticky`: below lg, AppShell is a fixed-height flex column
 * in which `<main>` is the only scrolling region, so a flex sibling placed
 * before `<main>` pins structurally.
 */
const MobileHeader: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.resolvedLanguage ?? i18n.language;

  const go = (path: string) => {
    setMenuOpen(false);
    navigate(path);
  };

  return (
    <header className="lg:hidden relative z-[900] shrink-0">
      {/* Announcement strip. A real button, because it navigates. */}
      <button
        type="button"
        onClick={() => navigate('/support')}
        className={
          'w-full block bg-brand-900 text-brand-100 border-0 cursor-pointer ' +
          'px-4 py-2 text-[10px] uppercase tracking-[0.16em] leading-tight ' +
          'transition-colors duration-150 active:text-accent-500'
        }
      >
        {t('announcement_help')}
      </button>

      <div className="flex items-center justify-between h-14 px-2 bg-brand-50 border-0 border-b border-solid border-brand-300">
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label={t('menu')}
          aria-expanded={menuOpen}
          className={
            'flex h-11 w-11 items-center justify-center bg-transparent border-0 ' +
            'cursor-pointer text-brand-900 text-[18px] ' +
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-500'
          }
        >
          <MenuIcon aria-hidden="true" />
        </button>

        <button
          type="button"
          onClick={() => navigate('/home')}
          aria-label={t('home')}
          className="flex items-center bg-transparent border-0 p-0 cursor-pointer"
        >
          <img src="/images/bonia/Bonia-Logo-01.png" alt="" className="h-8 w-auto" />
        </button>

        <button
          type="button"
          onClick={() => navigate('/account')}
          aria-label={t('account')}
          className={
            'flex h-11 w-11 items-center justify-center bg-transparent border-0 ' +
            'cursor-pointer text-brand-900 text-[18px] ' +
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-500'
          }
        >
          <AccountIcon aria-hidden="true" />
        </button>
      </div>

      {/*
        The drawer lists the same five destinations as the bottom nav, off the
        one NAV_ITEMS source, so the two cannot disagree. antd's Drawer handles
        the overlay, escape key and focus return.
      */}
      <Drawer
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        placement="left"
        width={288}
        title={<span className="font-display text-lg text-brand-900">{t('menu')}</span>}
        styles={{ body: { padding: 0 } }}
      >
        <nav aria-label="Mobile menu">
          <ul className="list-none m-0 p-0">
            {NAV_ITEMS.map(({ path, labelKey, Icon }) => {
              const active = pathname === path;
              return (
                <li key={path}>
                  <button
                    type="button"
                    onClick={() => go(path)}
                    aria-current={active ? 'page' : undefined}
                    className={
                      'w-full flex items-center gap-4 px-5 py-4 text-left cursor-pointer ' +
                      'bg-transparent border-0 border-b border-solid border-brand-300 ' +
                      'text-xs uppercase tracking-[0.16em] transition-colors duration-150 ' +
                      (active ? 'text-accent-600 font-semibold' : 'text-brand-900')
                    }
                  >
                    <span className={'w-5 flex justify-center text-[16px] ' + (active ? 'text-accent-600' : 'text-brand-600')}>
                      <Icon aria-hidden="true" />
                    </span>
                    {t(labelKey)}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/*
          Outside the <nav> above: switching language is not navigation, and
          keeping it out means the nav landmark still contains exactly the
          five destinations.

          The drawer is left open on purpose — the labels around it retranslate
          in place, which is the confirmation that the tap worked.
        */}
        <div className="px-5 py-6">
          <div className="text-[10px] uppercase tracking-[0.18em] text-brand-600 mb-3">
            {t('language')}
          </div>
          <div className="flex flex-wrap gap-2">
            {LANGUAGES.map(({ code, label }) => {
              const active = currentLang === code;
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => i18n.changeLanguage(code)}
                  aria-pressed={active}
                  className={
                    'px-3 py-2 rounded-full border border-solid cursor-pointer text-xs ' +
                    'transition-colors duration-150 ' +
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ' +
                    'focus-visible:outline-accent-500 ' +
                    (active
                      ? 'border-accent-500 bg-accent-500/15 text-accent-600 font-semibold'
                      : 'border-brand-300 bg-transparent text-brand-600')
                  }
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </Drawer>
    </header>
  );
};

export default MobileHeader;
